import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles, articleList } from '@/mocks/articles';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <i className="ri-article-line text-5xl text-foreground-300 mb-4 block"></i>
            <p className="text-foreground-500 mb-4">文章未找到</p>
            <Link to="/articles" className="text-primary-500 hover:text-primary-600 font-medium text-sm cursor-pointer">返回文章中心</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articleList.filter((a) => a.slug !== article.slug).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.date,
    author: { '@type': 'Organization', name: '久久久' },
    publisher: { '@type': 'Organization', name: '久久久' },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://www.jiujiujiu.com', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': 'https://www.jiujiujiu.com/articles', name: '文章中心' } },
      { '@type': 'ListItem', position: 3, item: { '@id': `https://www.jiujiujiu.com/article/${article.slug}`, name: article.title } },
    ],
  };

  const webpageSchema = {
    name: `${article.title} - 久久久`,
    description: article.excerpt,
    url: `https://www.jiujiujiu.com/article/${article.slug}`,
  };

  const renderContent = (html: string) => {
    return { __html: html };
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg type="WebPage" data={webpageSchema} />

      <Navbar />

      <main className="pt-24 md:pt-28">
        <article className="max-w-[900px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '文章中心', path: '/articles' },
              { label: article.title },
            ]}
          />

          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium">{article.category}</span>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400 flex items-center gap-1">
                <i className="ri-time-line"></i>{article.readTime}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap gap-1.5">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs text-foreground-500 px-2.5 py-1 rounded-full bg-background-100">{tag}</span>
              ))}
            </div>
          </div>

          <div className="mb-8 md:mb-10 rounded-xl overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-auto object-cover rounded-xl"
            />
          </div>

          {article.content.startsWith('<') ? (
            <div
              className="prose prose-base md:prose-lg max-w-none text-foreground-700 leading-relaxed article-content"
              dangerouslySetInnerHTML={renderContent(article.content)}
            />
          ) : (
            <div className="prose prose-base md:prose-lg max-w-none text-foreground-700 leading-relaxed article-content">
              {article.content.split('\n\n').map((paragraph, i) => {
                if (paragraph.startsWith('## ')) {
                  return <h2 key={i} className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mt-10 mb-4">{paragraph.replace('## ', '')}</h2>;
                }
                if (paragraph.startsWith('### ')) {
                  return <h3 key={i} className="text-lg font-heading font-semibold text-foreground-950 mt-8 mb-3">{paragraph.replace('### ', '')}</h3>;
                }
                return <p key={i} className="mb-4 leading-relaxed">{paragraph}</p>;
              })}
            </div>
          )}

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600">{tag}</span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-[900px] mx-auto px-4 md:px-6 py-12 md:py-16" aria-labelledby="related-heading">
            <h2 id="related-heading" className="text-xl font-heading font-semibold text-foreground-950 mb-6">相关文章</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300/60 transition-all duration-300 cursor-pointer"
                >
                  <div className="h-40 overflow-hidden">
                    <img src={ra.coverImage} alt={ra.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">{ra.title}</h3>
                    <p className="text-xs text-foreground-400 mt-2">{ra.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}